import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  List,
  ListItemButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import QrCodeIcon from "@mui/icons-material/QrCode";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplyIcon from "@mui/icons-material/Reply";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { getApiBaseUrl } from "../../utils/environment";
import { getAuthToken } from "../../services/apiClient";
import { useAuth } from "../../context/AuthContext";
import { UserRole } from "../../types/api-types";

interface ChatEmployee {
  id: string;
  tenantId: string;
  name: string;
  phoneNumber: string;
  whatsappChatName?: string;
  isActive: boolean;
}

interface TrainingFact {
  id: string;
  tenantId: string;
  question: string;
  answer: string;
  context?: string;
  isActive: boolean;
  updatedAt: string;
}

interface ChatMessage {
  id: string;
  role: string;
  source: string;
  content: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

interface ChatConversationSummary {
  id: string;
  tenantId: string;
  status: string;
  source?: string;
  visitorId?: string;
  customerPhoneNumber?: string;
  whatsappRecipient?: string;
  contactChannel?: string;
  contactStatus?: string;
  contactMetadata?: Record<string, unknown>;
  updatedAt: string;
  lastMessage?: ChatMessage;
  openEscalation?: {
    id: string;
    reference: string;
    status: string;
    question: string;
  };
}

interface ChatConversationDetail {
  session: ChatConversationSummary;
  messages: ChatMessage[];
  escalations: Array<Record<string, unknown>>;
}

interface WhatsappStatus {
  tenantId?: string;
  status?: string;
  qrDataUrl?: string;
  connectedNumber?: string;
  lastQrAt?: string;
  lastReadyAt?: string;
  lastHeartbeatAt?: string;
  error?: string;
}

interface WhatsappEmployeeGroupStatus {
  groupId?: string;
  title?: string;
  lastSyncedAt?: string;
  participantIds?: string[];
  participantsAdded?: string[];
  participantsRemoved?: string[];
  failedParticipants?: Record<string, unknown>;
  failedRemovals?: Record<string, unknown>;
  inviteLink?: string;
  invitedParticipants?: Record<string, string>;
  invitationMessageIds?: Record<string, string>;
  invitationFailures?: Record<string, string>;
  lastError?: string;
  lastErrorAt?: string;
}

interface WhatsappPairingStatus {
  tenantId?: string;
  paired?: boolean;
  serviceUrl?: string;
  appId?: string;
  callbackUrl?: string;
  pairedAt?: string;
}

const DEFAULT_TENANT_ID = "hobbygarage-frankenthal";

const emptyEmployee = {
  tenantId: DEFAULT_TENANT_ID,
  name: "",
  phoneNumber: "",
  isActive: true,
};

const emptyFact = {
  tenantId: DEFAULT_TENANT_ID,
  question: "",
  answer: "",
  context: "",
  isActive: true,
};

// Bringt eine Besucher-Kennung in eine lesbare Form. WhatsApp-Sessions tragen die
// Nummer als "whatsapp:491512345678@c.us"; daraus wird "+49 151 2345678".
function formatVisitor(visitorId?: string, fallbackId?: string): string {
  if (!visitorId) return fallbackId ? `Web-Chat ${fallbackId.slice(0, 8)}` : "Web-Chat";
  if (visitorId.startsWith("whatsapp:")) {
    const digits = visitorId.replace(/^whatsapp:/, "").replace(/@.*/, "").replace(/[^\d]/g, "");
    if (digits.startsWith("49") && digits.length >= 11) {
      return `+49 ${digits.slice(2, 5)} ${digits.slice(5)}`;
    }
    return digits ? `+${digits}` : "WhatsApp-Kontakt";
  }
  return visitorId;
}

function isWhatsappVisitor(visitorId?: string): boolean {
  return Boolean(visitorId?.startsWith("whatsapp:"));
}

function hasLinkedWhatsappContact(conversation?: ChatConversationSummary | null): boolean {
  return Boolean(conversation?.whatsappRecipient || conversation?.contactChannel === "website_whatsapp");
}

function channelLabel(conversation?: ChatConversationSummary | null): string {
  if (!conversation) return "Website";
  if (isWhatsappVisitor(conversation.visitorId)) return "WhatsApp";
  return hasLinkedWhatsappContact(conversation) ? "Website + WhatsApp" : "Website";
}

function contactStatusLabel(status?: string): string {
  if (status === "ACTIVE") return "WhatsApp aktiv";
  if (status === "FAILED") return "WhatsApp Fehler";
  if (status === "PENDING") return "WhatsApp ausstehend";
  if (status === "OFFERED") return "WhatsApp angeboten";
  return "Kein WhatsApp";
}

function contactStatusColor(status?: string): "default" | "success" | "warning" | "error" {
  if (status === "ACTIVE") return "success";
  if (status === "FAILED") return "error";
  if (status === "PENDING" || status === "OFFERED") return "warning";
  return "default";
}

function contactPhoneLabel(conversation?: ChatConversationSummary | null): string {
  const raw = conversation?.customerPhoneNumber || conversation?.whatsappRecipient || "";
  if (!raw) return "";
  return formatVisitor(`whatsapp:${raw}`, conversation?.id);
}

function messageChannelLabel(message: ChatMessage): string {
  const channel = message.metadata?.channel;
  if (channel === "whatsapp") return "WhatsApp";
  if (channel === "website") return "Website";
  if (message.source === "WHATSAPP") return "WhatsApp";
  if (message.source === "WORDPRESS") return "Website";
  return "";
}

function messageDeliveryLabel(message: ChatMessage): string {
  const delivery = message.metadata?.whatsappDelivery as { status?: string } | undefined;
  if (delivery?.status === "SENT") return "WhatsApp gesendet";
  if (delivery?.status === "FAILED") return "WhatsApp Fehler";
  return "";
}

function formatDateTime(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function whatsappStatusLabel(status?: string): string {
  if (status === "READY") return "Verbunden";
  if (status === "QR") return "QR wartet";
  if (status === "STARTING") return "Startet";
  if (status === "FAILED") return "Fehler";
  if (status === "STOPPED") return "Gestoppt";
  return "Unbekannt";
}

function whatsappStatusColor(status?: string): "default" | "success" | "warning" | "error" {
  if (status === "READY") return "success";
  if (status === "FAILED") return "error";
  if (status === "QR" || status === "STARTING" || status === "STOPPED") return "warning";
  return "default";
}

// Übersetzt Rolle/Quelle einer Nachricht in ein menschenlesbares Label + Optik.
function describeMessage(role: string, source: string, internal: boolean): {
  label: string;
  align: "start" | "end";
  variant: "customer" | "ai" | "team" | "system";
} {
  if (role === "USER") return { label: "Kunde", align: "start", variant: "customer" };
  if (internal || role === "HUMAN") return { label: "Team (intern)", align: "end", variant: "team" };
  if (source === "KI_GATEWAY") return { label: "KI-Assistent", align: "end", variant: "ai" };
  if (role === "ASSISTANT") return { label: "Antwort an Kunde", align: "end", variant: "ai" };
  return { label: "System", align: "start", variant: "system" };
}

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Aktiv",
  WAITING_HUMAN: "Wartet auf Team",
  CLOSED: "Geschlossen",
};

const ChatSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;
  const [tab, setTab] = useState(0);
  const tenantId = DEFAULT_TENANT_ID;
  const [employees, setEmployees] = useState<ChatEmployee[]>([]);
  const [facts, setFacts] = useState<TrainingFact[]>([]);
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [conversationFilter, setConversationFilter] = useState<"all" | "waiting" | "whatsapp_active" | "whatsapp_failed">("all");
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [conversationDetail, setConversationDetail] = useState<ChatConversationDetail | null>(null);
  const [replyText, setReplyText] = useState("");
  const [employeeForm, setEmployeeForm] = useState(emptyEmployee);
  const [factForm, setFactForm] = useState(emptyFact);
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [editingFactId, setEditingFactId] = useState<string | null>(null);
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsappStatus>({});
  const [employeeGroupStatus, setEmployeeGroupStatus] = useState<WhatsappEmployeeGroupStatus>({});
  const [employeeGroupTitle, setEmployeeGroupTitle] = useState("");
  const [syncingEmployeeGroup, setSyncingEmployeeGroup] = useState(false);
  const [savingEmployeeGroupTitle, setSavingEmployeeGroupTitle] = useState(false);
  const [pairServiceUrl, setPairServiceUrl] = useState("https://whatsapp.csnetworkx.de");
  const [pairingKey, setPairingKey] = useState("");
  const [pairingInfo, setPairingInfo] = useState<WhatsappPairingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [wsStatus, setWsStatus] = useState<"connected" | "connecting" | "fallback">("connecting");
  const [loading, setLoading] = useState(false);
  const selectedConversationIdRef = useRef<string | null>(null);
  const reloadTimerRef = useRef<number | null>(null);
  const wsConnectedRef = useRef(false);

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const request = useCallback(async (path: string, options: RequestInit = {}) => {
    const token = getAuthToken();
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers ?? {}),
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${response.status}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }, [apiBaseUrl]);

  const loadEmployees = useCallback(async () => {
    const body = await request(`/chat/employees?tenantId=${encodeURIComponent(tenantId)}`);
    setEmployees(body.data ?? []);
  }, [request, tenantId]);

  const loadFacts = useCallback(async () => {
    const body = await request(`/chat/training-facts?tenantId=${encodeURIComponent(tenantId)}`);
    setFacts(body.data ?? []);
  }, [request, tenantId]);

  const loadConversations = useCallback(async () => {
    const body = await request(`/chat/conversations?tenantId=${encodeURIComponent(tenantId)}`);
    setConversations(body.data ?? []);
  }, [request, tenantId]);

  const loadConversationDetail = useCallback(async (sessionId: string) => {
    const body = await request(`/chat/conversations/${encodeURIComponent(sessionId)}`);
    setConversationDetail(body.data ?? null);
    setSelectedConversationId(sessionId);
  }, [request]);

  const loadWhatsappStatus = useCallback(async () => {
    const body = await request(`/chat/whatsapp/sessions/${encodeURIComponent(tenantId)}/status`);
    setWhatsappStatus(body.data ?? {});
  }, [request, tenantId]);

  const loadEmployeeGroupStatus = useCallback(async () => {
    const body = await request(`/chat/whatsapp/groups/${encodeURIComponent(tenantId)}/status`);
    const data = body.data ?? {};
    setEmployeeGroupStatus(data);
    setEmployeeGroupTitle(data.title || "");
  }, [request, tenantId]);

  const loadPairingStatus = useCallback(async () => {
    const body = await request(`/chat/whatsapp/pairing/${encodeURIComponent(tenantId)}`);
    const data = body.data ?? {};
    setPairingInfo(data);
    if (data.serviceUrl) {
      setPairServiceUrl(data.serviceUrl);
    }
  }, [request, tenantId]);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await Promise.all([
        loadEmployees(),
        loadFacts(),
        loadConversations(),
        loadPairingStatus(),
        loadWhatsappStatus(),
        loadEmployeeGroupStatus(),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chat-Daten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [loadEmployees, loadFacts, loadConversations, loadPairingStatus, loadWhatsappStatus, loadEmployeeGroupStatus]);

  useEffect(() => {
    void reloadAll();
  }, [reloadAll]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  // Nach jeder Aktualisierung des Verlaufs ans Ende scrollen (neueste Nachricht).
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [conversationDetail?.messages.length, selectedConversationId]);

  const scheduleConversationReload = useCallback((changedSessionId?: string) => {
    if (reloadTimerRef.current) {
      window.clearTimeout(reloadTimerRef.current);
    }

    reloadTimerRef.current = window.setTimeout(() => {
      reloadTimerRef.current = null;
      void (async () => {
        await loadConversations();
        const selectedId = selectedConversationIdRef.current;
        if (selectedId && (!changedSessionId || changedSessionId === selectedId)) {
          await loadConversationDetail(selectedId);
        }
      })().catch((err) => {
        setError(err instanceof Error ? err.message : "Konversationen konnten nicht aktualisiert werden");
      });
    }, 150);
  }, [loadConversationDetail, loadConversations]);

  const buildChatWebSocketUrl = useCallback(() => {
    const token = getAuthToken();
    const url = apiBaseUrl.startsWith("http")
      ? new URL(apiBaseUrl)
      : new URL(apiBaseUrl, window.location.origin);

    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `${url.pathname.replace(/\/$/, "")}/chat/ws`;
    url.searchParams.set("tenantId", tenantId);
    url.searchParams.set("token", token);
    return url.toString();
  }, [apiBaseUrl, tenantId]);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let fallbackTimer: number | null = null;
    let manuallyClosed = false;
    let reconnectAttempt = 0;

    const connect = () => {
      if (manuallyClosed) return;
      const token = getAuthToken();
      if (!token) {
        setWsStatus("fallback");
        reconnectTimer = window.setTimeout(connect, 5000);
        return;
      }

      setWsStatus((current) => current === "connected" ? current : "connecting");
      socket = new WebSocket(buildChatWebSocketUrl());

      socket.onopen = () => {
        reconnectAttempt = 0;
        wsConnectedRef.current = true;
        setWsStatus("connected");
        socket?.send(JSON.stringify({ type: "subscribe", tenantId }));
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === "chat" && payload.tenantId === tenantId) {
            scheduleConversationReload(typeof payload.sessionId === "string" ? payload.sessionId : undefined);
          }
        } catch {
          // Ignore malformed realtime messages; REST reload remains the source of truth.
        }
      };

      socket.onclose = () => {
        if (manuallyClosed) return;
        wsConnectedRef.current = false;
        setWsStatus("fallback");
        const delay = Math.min(30000, 1000 * 2 ** reconnectAttempt);
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        socket?.close();
      };
    };

    connect();
    fallbackTimer = window.setInterval(() => {
      if (!wsConnectedRef.current) {
        scheduleConversationReload();
      }
    }, 30000);

    return () => {
      manuallyClosed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (fallbackTimer) window.clearInterval(fallbackTimer);
      if (reloadTimerRef.current) window.clearTimeout(reloadTimerRef.current);
      wsConnectedRef.current = false;
      socket?.close();
    };
  }, [buildChatWebSocketUrl, scheduleConversationReload, tenantId]);

  const resetEmployeeForm = () => {
    setEmployeeForm({ ...emptyEmployee, tenantId });
    setEditingEmployeeId(null);
  };

  const resetFactForm = () => {
    setFactForm({ ...emptyFact, tenantId });
    setEditingFactId(null);
  };

  const submitEmployee = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const payload = { ...employeeForm, tenantId };
      if (editingEmployeeId) {
        await request(`/chat/employees/${editingEmployeeId}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await request("/chat/employees", { method: "POST", body: JSON.stringify(payload) });
      }
      resetEmployeeForm();
      await Promise.all([loadEmployees(), loadEmployeeGroupStatus()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mitarbeiter konnte nicht gespeichert werden");
    }
  };

  const syncEmployeeGroup = async () => {
    setError(null);
    setNotice(null);
    setSyncingEmployeeGroup(true);
    try {
      const body = await request(`/chat/whatsapp/groups/${encodeURIComponent(tenantId)}/sync`, { method: "POST" });
      setEmployeeGroupStatus(body.data ?? {});
      setNotice("WhatsApp-Mitarbeitergruppe wurde synchronisiert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "WhatsApp-Mitarbeitergruppe konnte nicht synchronisiert werden");
    } finally {
      setSyncingEmployeeGroup(false);
    }
  };

  const saveEmployeeGroupTitle = async () => {
    const title = employeeGroupTitle.trim();
    if (!title) {
      setError("Gruppenname ist erforderlich");
      return;
    }

    setError(null);
    setNotice(null);
    setSavingEmployeeGroupTitle(true);
    try {
      const body = await request(`/chat/whatsapp/groups/${encodeURIComponent(tenantId)}/title`, {
        method: "PUT",
        body: JSON.stringify({ title }),
      });
      setEmployeeGroupStatus(body.data ?? {});
      setEmployeeGroupTitle(body.data?.title || title);
      setNotice("WhatsApp-Gruppenname wurde gespeichert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "WhatsApp-Gruppenname konnte nicht gespeichert werden");
    } finally {
      setSavingEmployeeGroupTitle(false);
    }
  };

  const submitFact = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const payload = { ...factForm, tenantId };
      if (editingFactId) {
        await request(`/chat/training-facts/${editingFactId}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await request("/chat/training-facts", { method: "POST", body: JSON.stringify(payload) });
      }
      resetFactForm();
      await loadFacts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wissenseintrag konnte nicht gespeichert werden");
    }
  };

  const startWhatsapp = async () => {
    setError(null);
    setNotice(null);
    try {
      const body = await request(`/chat/whatsapp/sessions/${encodeURIComponent(tenantId)}/start`, { method: "POST" });
      setWhatsappStatus(body.data ?? {});
      await refreshQr();
      setNotice("WhatsApp-Session wurde gestartet.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "WhatsApp konnte nicht gestartet werden");
    }
  };

  const pairWhatsappService = async () => {
    setError(null);
    setNotice(null);
    setPairingInfo(null);
    try {
      const body = await request("/chat/whatsapp/pair", {
        method: "POST",
        body: JSON.stringify({
          tenantId,
          serviceUrl: pairServiceUrl,
          pairingKey,
          appName: `Mietwerkstatt ${tenantId}`,
        }),
      });
      setPairingInfo({ ...(body.data ?? {}), paired: true, serviceUrl: pairServiceUrl });
      setPairingKey("");
      await loadWhatsappStatus();
      setNotice("WhatsApp-Service wurde gekoppelt.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "WhatsApp-Service konnte nicht gekoppelt werden");
    }
  };

  const refreshQr = async () => {
    const body = await request(`/chat/whatsapp/sessions/${encodeURIComponent(tenantId)}/qr`);
    setWhatsappStatus(body.data ?? {});
  };

  const logoutWhatsapp = async () => {
    if (!window.confirm("WhatsApp-Verbindung wirklich aus der Werkstatt-App entfernen? Danach ist eine neue Kopplung nötig.")) return;
    setError(null);
    setNotice(null);
    try {
      const body = await request(`/chat/whatsapp/sessions/${encodeURIComponent(tenantId)}/logout`, { method: "POST" });
      setWhatsappStatus(body.data ?? {});
      if (body.data?.pairingCleared) {
        await loadPairingInfo();
      }
      setNotice("Die WhatsApp-Kopplung wurde aus der Werkstatt-App entfernt. Der Service kann jetzt neu gekoppelt werden.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "WhatsApp konnte nicht abgemeldet werden");
    }
  };

  const sendManualReply = async () => {
    if (!selectedConversationId || !replyText.trim()) return;
    setError(null);
    setNotice(null);
    try {
      await request(`/chat/conversations/${encodeURIComponent(selectedConversationId)}/reply`, {
        method: "POST",
        body: JSON.stringify({ answer: replyText.trim() }),
      });
      setReplyText("");
      await Promise.all([loadConversations(), loadConversationDetail(selectedConversationId)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Antwort konnte nicht gesendet werden");
    }
  };

  const retryWhatsappContact = async () => {
    if (!selectedConversationId || !conversationDetail) return;
    const phoneNumber = conversationDetail.session.customerPhoneNumber || conversationDetail.session.whatsappRecipient || "";
    setError(null);
    setNotice(null);
    try {
      await request(`/chat/conversations/${encodeURIComponent(selectedConversationId)}/contact/whatsapp`, {
        method: "POST",
        body: JSON.stringify({ phoneNumber }),
      });
      await Promise.all([loadConversations(), loadConversationDetail(selectedConversationId)]);
      setNotice("WhatsApp-Rückkontakt wurde erneut angestoßen.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "WhatsApp-Rückkontakt konnte nicht erneut gesendet werden");
    }
  };

  const deleteConversation = async (sessionId: string) => {
    if (!window.confirm("Konversation wirklich löschen? Wichtige gelöste Antworten werden vorher in den Wissensspeicher übernommen.")) return;
    setError(null);
    setNotice(null);
    try {
      await request(`/chat/conversations/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
      if (selectedConversationId === sessionId) {
        setSelectedConversationId(null);
        setConversationDetail(null);
      }
      await loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Konversation konnte nicht gelöscht werden");
    }
  };

  const cleanupConversations = async () => {
    if (!window.confirm("Alle erledigten Konversationen löschen, die älter als 24 Stunden sind?")) return;
    setError(null);
    try {
      const body = await request("/chat/conversations/cleanup", {
        method: "POST",
        body: JSON.stringify({ tenantId, olderThanHours: 24 }),
      });
      await Promise.all([loadConversations(), loadFacts()]);
      const deleted = body.data?.deleted ?? 0;
      const transferredFacts = body.data?.transferredFacts ?? 0;
      setNotice(`Bereinigung abgeschlossen: ${deleted} gelöscht, ${transferredFacts} Wissenseinträge übernommen.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Konversationen konnten nicht bereinigt werden");
    }
  };

  const deactivateTrainingFact = async (fact: TrainingFact) => {
    if (!window.confirm(`Wissenseintrag "${fact.question}" deaktivieren?`)) return;
    setError(null);
    setNotice(null);
    try {
      await request(`/chat/training-facts/${fact.id}`, { method: "DELETE" });
      await loadFacts();
      setNotice("Wissenseintrag wurde deaktiviert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wissenseintrag konnte nicht deaktiviert werden");
    }
  };

  const permanentlyDeleteTrainingFact = async (fact: TrainingFact) => {
    if (!window.confirm(
      `Wissenseintrag "${fact.question}" endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    setError(null);
    setNotice(null);
    try {
      await request(`/chat/training-facts/${fact.id}/permanent`, { method: "DELETE" });
      if (editingFactId === fact.id) resetFactForm();
      await loadFacts();
      setNotice("Wissenseintrag wurde endgültig gelöscht.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wissenseintrag konnte nicht endgültig gelöscht werden");
    }
  };

  const statusColor = whatsappStatusColor(whatsappStatus.status);
  const isWhatsappPaired = Boolean(pairingInfo?.paired);
  const isWhatsappReady = whatsappStatus.status === "READY";
  const conversationsWaiting = conversations.filter((conversation) => conversation.status === "WAITING_HUMAN").length;
  const conversationsWhatsappActive = conversations.filter((conversation) => conversation.contactStatus === "ACTIVE").length;
  const conversationsWhatsappFailed = conversations.filter((conversation) => conversation.contactStatus === "FAILED").length;
  const activeEmployees = employees.filter((employee) => employee.isActive).length;
  const WhatsappStateIcon = isWhatsappReady
    ? CheckCircleOutlineIcon
    : whatsappStatus.status === "FAILED"
      ? ErrorOutlineIcon
      : isWhatsappPaired
        ? QrCodeIcon
        : LinkOffIcon;
  const whatsappStateTitle = !isWhatsappPaired
    ? "Service nicht gekoppelt"
    : isWhatsappReady
      ? "WhatsApp ist verbunden"
      : whatsappStatus.status === "FAILED"
        ? "Session gestört"
        : "Session wartet auf Verbindung";
  const whatsappStateDescription = !isWhatsappPaired
    ? "Kopple zuerst den externen WhatsApp-Service."
    : isWhatsappReady
      ? "Eskalierte Chat-Rückfragen können per WhatsApp verarbeitet werden."
      : "Starte die Session und scanne den QR-Code mit dem vorgesehenen WhatsApp-Konto.";
  const whatsappAccentColor = isWhatsappReady
    ? "success.main"
    : whatsappStatus.status === "FAILED"
      ? "error.main"
      : isWhatsappPaired
        ? "warning.main"
        : "grey.500";
  const filteredConversations = useMemo(() => {
    if (conversationFilter === "waiting") {
      return conversations.filter((conversation) => conversation.status === "WAITING_HUMAN");
    }
    if (conversationFilter === "whatsapp_active") {
      return conversations.filter((conversation) => conversation.contactStatus === "ACTIVE");
    }
    if (conversationFilter === "whatsapp_failed") {
      return conversations.filter((conversation) => conversation.contactStatus === "FAILED");
    }
    return conversations;
  }, [conversationFilter, conversations]);
  const activeFacts = facts.filter((fact) => fact.isActive).length;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: "100%", overflow: "hidden" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isSmallScreen ? "h5" : "h4"} gutterBottom>Chat & WhatsApp</Typography>
          <Typography color="text.secondary">
            Eingehende Chats, WhatsApp-Eskalation, Team-Ziele und Wissensspeicher verwalten.
          </Typography>
        </Box>
        <TextField
          label="Werkstatt / Tenant"
          value={tenantId}
          inputProps={{ readOnly: true }}
          sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Paper sx={{ mb: 3, maxWidth: "100%", overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              minWidth: { xs: "auto", sm: 140 },
              px: { xs: 1.5, sm: 2 },
              whiteSpace: "nowrap",
            },
          }}
        >
          <Tab label="Konversationen" />
          <Tab label="WhatsApp" />
          <Tab label="Mitarbeiter" />
          <Tab label="Wissensdatenbank" />
        </Tabs>
      </Paper>

      {tab === 1 && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ p: { xs: 1.75, sm: 2.5 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar sx={{ bgcolor: whatsappAccentColor, width: 44, height: 44, flexShrink: 0 }}>
                  <WhatsappStateIcon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    WhatsApp-Service
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verbindung, Session-Status und Wiederherstellung
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip label={whatsappStatusLabel(whatsappStatus.status)} color={statusColor} />
                <Chip
                  label={isWhatsappPaired ? "Service gekoppelt" : "Nicht gekoppelt"}
                  color={isWhatsappPaired ? "success" : "warning"}
                  variant="outlined"
                  icon={isWhatsappPaired ? <LinkIcon /> : <LinkOffIcon />}
                />
              </Stack>
            </Stack>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ p: { xs: 1.75, sm: 2.5 } }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {whatsappStateTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {whatsappStateDescription}
                </Typography>
              </Box>
              {isAdmin && isWhatsappPaired && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadWhatsappStatus}>
                    Status prüfen
                  </Button>
                  {!isWhatsappReady && (
                    <Button variant="contained" startIcon={<QrCodeIcon />} onClick={startWhatsapp}>
                      {whatsappStatus.qrDataUrl ? "QR erneuern" : "Session starten"}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
            {isAdmin && (
              <Box sx={{ px: { xs: 1.75, sm: 2.5 }, pb: { xs: 1.75, sm: 2.5 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                >
                  <TextField
                    label="Supportgruppenname"
                    value={employeeGroupTitle}
                    onChange={(event) => setEmployeeGroupTitle(event.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                    helperText="Beim Synchronisieren wird eine Gruppe mit diesem Namen gesucht oder erstellt."
                  />
                  <Button
                    variant="outlined"
                    onClick={saveEmployeeGroupTitle}
                    disabled={savingEmployeeGroupTitle || !employeeGroupTitle.trim()}
                    sx={{ minWidth: { xs: "auto", md: 120 } }}
                  >
                    {savingEmployeeGroupTitle ? "Speichert..." : "Speichern"}
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>

          {!isAdmin && (
            <Alert severity="warning">
              Dieser Bereich ist nur für Administratoren sichtbar.
            </Alert>
          )}

          {isAdmin && !isWhatsappPaired && (
            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
              <Stack sx={{ p: { xs: 1.75, sm: 2.5 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Service koppeln
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pairing-Code im WhatsApp-Service erzeugen und hier eintragen.
                </Typography>
              </Stack>
              <Box
                component="form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void pairWhatsappService();
                }}
                sx={{ p: { xs: 1.75, sm: 2.5 } }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 1fr) minmax(220px, 0.8fr) 150px" },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    label="WhatsApp-Service URL"
                    value={pairServiceUrl}
                    onChange={(event) => setPairServiceUrl(event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Pairing-Code"
                    value={pairingKey}
                    onChange={(event) => setPairingKey(event.target.value)}
                    type="password"
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<LinkIcon />}
                    disabled={!pairServiceUrl.trim() || !pairingKey.trim()}
                    sx={{ minHeight: 56 }}
                  >
                    Koppeln
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {isAdmin && isWhatsappPaired && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.1fr) minmax(340px, 0.9fr)" },
                gap: 3,
                alignItems: "start",
              }}
            >
              <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ p: { xs: 1.75, sm: 2 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Session-Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Laufzeitdaten des verbundenen WhatsApp-Clients
                    </Typography>
                  </Box>
                  <Chip label={whatsappStatus.status || "UNKNOWN"} color={statusColor} variant="outlined" />
                </Stack>
                <Stack divider={<Divider flexItem />} sx={{ p: { xs: 1.75, sm: 2 } }}>
                  {[
                    ["Tenant", whatsappStatus.tenantId || tenantId],
                    ["Gekoppelte Nummer", whatsappStatus.connectedNumber ? formatVisitor(`whatsapp:${whatsappStatus.connectedNumber}@c.us`) : "-"],
                    ["Letzter Heartbeat", formatDateTime(whatsappStatus.lastHeartbeatAt)],
                    ["Bereit seit", formatDateTime(whatsappStatus.lastReadyAt)],
                    ["Letzter QR", formatDateTime(whatsappStatus.lastQrAt)],
                    ["Gekoppelt seit", formatDateTime(pairingInfo?.pairedAt)],
                    ["Service URL", pairingInfo?.serviceUrl || "-"],
                    ["Callback URL", pairingInfo?.callbackUrl || "-"],
                    ["App-ID", pairingInfo?.appId || "-"],
                  ].map(([label, value]) => (
                    <Stack
                      key={label}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.75}
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", sm: "center" }}
                      sx={{ py: 1.15 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ textAlign: { xs: "left", sm: "right" }, overflowWrap: "anywhere" }}
                      >
                        {value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
                <Stack sx={{ p: { xs: 1.75, sm: 2 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Wiederherstellung
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Session prüfen, QR neu starten oder WhatsApp abmelden
                  </Typography>
                </Stack>
                <Stack spacing={2} sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Alert severity={isWhatsappReady ? "success" : whatsappStatus.status === "FAILED" ? "error" : "info"}>
                    {isWhatsappReady
                      ? "Die Session ist einsatzbereit."
                      : whatsappStatus.status === "FAILED"
                        ? "Die Session meldet einen Fehler."
                        : "Die Session wartet auf einen QR-Scan oder Neustart."}
                  </Alert>

                  {whatsappStatus.error && (
                    <Alert severity="warning">
                      {whatsappStatus.error}
                    </Alert>
                  )}

                  {!isWhatsappReady && whatsappStatus.qrDataUrl && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        QR-Code scannen
                      </Typography>
                      <Box
                        component="img"
                        src={whatsappStatus.qrDataUrl}
                        alt="WhatsApp QR"
                        sx={{
                          display: "block",
                          width: "100%",
                          maxWidth: 300,
                          aspectRatio: "1 / 1",
                          objectFit: "contain",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          bgcolor: "common.white",
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        Erzeugt: {formatDateTime(whatsappStatus.lastQrAt)}
                      </Typography>
                    </Box>
                  )}

                  <Stack direction={{ xs: "column", sm: "row", lg: "column" }} spacing={1}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadWhatsappStatus} fullWidth>
                      Status prüfen
                    </Button>
                    {!isWhatsappReady && (
                      <Button variant="contained" startIcon={<QrCodeIcon />} onClick={startWhatsapp} fullWidth>
                        {whatsappStatus.qrDataUrl ? "QR erneuern" : "Session starten"}
                      </Button>
                    )}
                    <Button color="error" variant="outlined" startIcon={<LogoutIcon />} onClick={logoutWhatsapp} fullWidth>
                      Session abmelden
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          )}
        </Stack>
      )}

      {tab === 2 && (
        <>
          <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ p: { xs: 1.75, sm: 2.5 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Mitarbeiter</Typography>
                <Typography variant="body2" color="text.secondary">
                  Aktive Ziele erhalten interne WhatsApp-Rückfragen aus eskalierten Chats.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  label={activeEmployees > 0 ? `${activeEmployees} aktiv` : "Keine aktiven Ziele"}
                  color={activeEmployees > 0 ? "success" : "warning"}
                  variant="outlined"
                />
                <Chip label={`${employees.length} gesamt`} variant="outlined" />
              </Stack>
            </Stack>

            <Box component="form" onSubmit={submitEmployee} sx={{ p: { xs: 1.75, sm: 2.5 } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {editingEmployeeId ? "Ziel bearbeiten" : "Neues WhatsApp-Ziel"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(180px, 1.2fr) minmax(180px, 1fr)",
                  },
                  gap: 2,
                  alignItems: "start",
                }}
              >
                <TextField
                  label="Name"
                  value={employeeForm.name}
                  onChange={(event) => setEmployeeForm({ ...employeeForm, name: event.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Telefonnummer"
                  value={employeeForm.phoneNumber}
                  onChange={(event) => setEmployeeForm({ ...employeeForm, phoneNumber: event.target.value })}
                  required
                  fullWidth
                />
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={employeeForm.isActive}
                      onChange={(event) => setEmployeeForm({ ...employeeForm, isActive: event.target.checked })}
                    />
                  }
                  label="Ziel aktiv"
                  sx={{ m: 0 }}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                  {editingEmployeeId && (
                    <Button type="button" variant="outlined" onClick={resetEmployeeForm}>
                      Abbrechen
                    </Button>
                  )}
                  <Button type="submit" variant="contained" sx={{ minWidth: 140 }}>
                    {editingEmployeeId ? "Speichern" : "Anlegen"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ p: { xs: 1.75, sm: 2 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <WhatsAppIcon color="success" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>Mitarbeitergruppe</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Interne Chat-Anfragen werden in diese WhatsApp-Gruppe gesendet.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={syncEmployeeGroup}
                disabled={syncingEmployeeGroup}
              >
                {syncingEmployeeGroup ? "Synchronisiere..." : "Gruppe synchronisieren"}
              </Button>
            </Stack>
            <Box sx={{ p: { xs: 1.75, sm: 2 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={employeeGroupStatus.groupId ? "success" : "warning"}
                  variant="outlined"
                  label={employeeGroupStatus.groupId ? "Gruppe verbunden" : "Keine Gruppe"}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${employeeGroupStatus.participantIds?.length ?? 0} Teilnehmer geplant`}
                />
                <Chip
                  size="small"
                  color={Object.keys(employeeGroupStatus.failedParticipants ?? {}).length ? "warning" : "default"}
                  variant="outlined"
                  label={`${Object.keys(employeeGroupStatus.failedParticipants ?? {}).length} Hinzufügen fehlgeschlagen`}
                />
                <Chip
                  size="small"
                  color={Object.keys(employeeGroupStatus.failedRemovals ?? {}).length ? "warning" : "default"}
                  variant="outlined"
                  label={`${Object.keys(employeeGroupStatus.failedRemovals ?? {}).length} Entfernen fehlgeschlagen`}
                />
              </Stack>
              <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Gruppen-ID: {employeeGroupStatus.groupId || "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Letzte Synchronisierung: {formatDateTime(employeeGroupStatus.lastSyncedAt)}
                </Typography>
                {employeeGroupStatus.lastError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {employeeGroupStatus.lastError}
                  </Alert>
                )}
                {Object.keys(employeeGroupStatus.failedParticipants ?? {}).length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight={700} gutterBottom>
                      Diese Nummern konnten nicht direkt hinzugefügt werden:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {Object.entries(employeeGroupStatus.failedParticipants ?? {}).map(([participant, info]) => {
                        const message =
                          info && typeof info === "object" && typeof (info as { message?: unknown }).message === "string"
                            ? (info as { message: string }).message
                            : "Unbekannter Fehler";
                        const invitedAt = employeeGroupStatus.invitedParticipants?.[participant];
                        const invitationMessageId = employeeGroupStatus.invitationMessageIds?.[participant];
                        const invitationFailure = employeeGroupStatus.invitationFailures?.[participant];
                        return (
                          <li key={participant}>
                            <Typography variant="body2" component="span">
                              {participant.replace(/@c\.us$/, "")}: {message}
                            </Typography>
                            {invitedAt && invitationMessageId && (
                              <Typography variant="body2" component="span" sx={{ color: "success.main" }}>
                                {" "}· An WhatsApp übergeben ({formatDateTime(invitedAt)})
                              </Typography>
                            )}
                            {invitationFailure && (
                              <Typography variant="body2" component="span" sx={{ color: "error.main" }}>
                                {" "}· Versand fehlgeschlagen: {invitationFailure}
                              </Typography>
                            )}
                          </li>
                        );
                      })}
                    </Box>
                    {employeeGroupStatus.inviteLink && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography variant="body2" gutterBottom>
                          Der Einladungslink wird automatisch an WhatsApp übergeben. Eine tatsächliche Zustellung an das Endgerät kann dabei nicht bestätigt werden. Bei Bedarf manuell teilen:
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                          <MuiLink href={employeeGroupStatus.inviteLink} target="_blank" rel="noopener" sx={{ wordBreak: "break-all" }}>
                            {employeeGroupStatus.inviteLink}
                          </MuiLink>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              void navigator.clipboard?.writeText(employeeGroupStatus.inviteLink || "");
                            }}
                          >
                            Link kopieren
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Alert>
                )}
              </Stack>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, boxShadow: "none" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ p: { xs: 1.75, sm: 2 }, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>Ziele</Typography>
                <Typography variant="body2" color="text.secondary">
                  Alle aktiven Ziele werden in die WhatsApp-Mitarbeitergruppe synchronisiert.
                </Typography>
              </Box>
              <Chip size="small" label={`${activeEmployees}/${employees.length} aktiv`} color={activeEmployees > 0 ? "success" : "default"} variant="outlined" />
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Telefonnummer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: employee.isActive ? "success.main" : "grey.400", flexShrink: 0 }}>
                            {employee.whatsappChatName ? <WhatsAppIcon sx={{ fontSize: 18 }} /> : <PersonIcon sx={{ fontSize: 18 }} />}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700} noWrap>{employee.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Ziel-ID {employee.id.slice(0, 8)}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{employee.phoneNumber}</TableCell>
                      <TableCell>
                        <Chip size="small" label={employee.isActive ? "Aktiv" : "Inaktiv"} color={employee.isActive ? "success" : "default"} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Bearbeiten">
                          <IconButton onClick={() => {
                            setEditingEmployeeId(employee.id);
                            setEmployeeForm({
                              tenantId,
                              name: employee.name,
                              phoneNumber: employee.phoneNumber,
                              isActive: employee.isActive,
                            });
                          }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton color="error" onClick={async () => {
                            if (!window.confirm(`${employee.name} wirklich löschen?`)) return;
                            await request(`/chat/employees/${employee.id}`, { method: "DELETE" });
                            await Promise.all([loadEmployees(), loadEmployeeGroupStatus()]);
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && employees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Stack alignItems="center" spacing={1} sx={{ py: 5, color: "text.secondary" }}>
                          <WhatsAppIcon sx={{ fontSize: 42, opacity: 0.45 }} />
                          <Typography variant="body2">Noch keine Chat-Mitarbeiter angelegt.</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {tab === 0 && (
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems="stretch"
            sx={{ minHeight: { lg: "calc(100vh - 260px)" } }}
          >
          {/* Liste – auf Mobil ausgeblendet, sobald ein Chat geöffnet ist. */}
          <Paper
            variant="outlined"
            sx={{
              width: { xs: "100%", lg: 420 },
              flexShrink: 0,
              overflow: "hidden",
              display: isMobile && conversationDetail ? "none" : "block",
              borderRadius: 1,
              boxShadow: "none",
              borderColor: "divider",
            }}
          >
            <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "grey.50" }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Konversationen</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Live-Eingang &amp; manuelle Bearbeitung
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={wsStatus === "connected" ? "Live" : "Fallback"}
                  color={wsStatus === "connected" ? "success" : "warning"}
                  variant="outlined"
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`${conversations.length} Chats`}
                    color={conversationFilter === "all" ? "primary" : "default"}
                    variant={conversationFilter === "all" ? "filled" : "outlined"}
                    onClick={() => setConversationFilter("all")}
                  />
                  <Chip
                    size="small"
                    label={`${conversationsWaiting} offen`}
                    color={conversationFilter === "waiting" ? "warning" : "default"}
                    variant={conversationFilter === "waiting" ? "filled" : "outlined"}
                    onClick={() => setConversationFilter("waiting")}
                  />
                  <Chip
                    size="small"
                    label={`${conversationsWhatsappActive} WA aktiv`}
                    color={conversationFilter === "whatsapp_active" ? "success" : "default"}
                    variant={conversationFilter === "whatsapp_active" ? "filled" : "outlined"}
                    onClick={() => setConversationFilter("whatsapp_active")}
                  />
                  <Chip
                    size="small"
                    label={`${conversationsWhatsappFailed} WA Fehler`}
                    color={conversationFilter === "whatsapp_failed" ? "error" : "default"}
                    variant={conversationFilter === "whatsapp_failed" ? "filled" : "outlined"}
                    onClick={() => setConversationFilter("whatsapp_failed")}
                  />
                </Stack>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={cleanupConversations}
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                >
                  24h bereinigen
                </Button>
              </Stack>
            </Stack>
            <Divider />
            <List
              disablePadding
              sx={{
                maxHeight: {
                  xs: "calc(100vh - 300px)",
                  sm: "calc(100vh - 320px)",
                  lg: "calc(100vh - 420px)",
                },
                minHeight: { xs: 320, lg: 420 },
                overflow: "auto",
              }}
            >
              {filteredConversations.map((conversation) => {
                const waiting = conversation.status === "WAITING_HUMAN";
                const whatsapp = isWhatsappVisitor(conversation.visitorId);
                const linkedWhatsapp = hasLinkedWhatsappContact(conversation);
                return (
                  <ListItemButton
                    key={conversation.id}
                    selected={selectedConversationId === conversation.id}
                    onClick={() => void loadConversationDetail(conversation.id)}
                    sx={{
                      alignItems: "flex-start",
                      gap: { xs: 1, sm: 1.5 },
                      py: 1.5,
                      px: { xs: 1.5, sm: 2 },
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      position: "relative",
                      bgcolor: "background.paper",
                      "&.Mui-selected": {
                        bgcolor: "rgba(102, 126, 234, 0.08)",
                      },
                      "&.Mui-selected::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 10,
                        bottom: 10,
                        width: 3,
                        borderRadius: "0 3px 3px 0",
                        bgcolor: "primary.main",
                      },
                      "&:hover": {
                        bgcolor: "rgba(102, 126, 234, 0.06)",
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: whatsapp || linkedWhatsapp ? "success.main" : "primary.main",
                        width: 40,
                        height: 40,
                        mt: 0.5,
                        boxShadow: "0 1px 2px rgba(15,23,42,0.18)",
                      }}
                    >
                      {whatsapp || linkedWhatsapp ? <WhatsAppIcon /> : <PersonIcon />}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {formatVisitor(conversation.visitorId, conversation.id)}
                        </Typography>
                        {waiting && <Chip size="small" color="warning" label="offen" sx={{ height: 22, flexShrink: 0 }} />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {conversation.lastMessage?.content?.slice(0, 80) || "Keine Nachrichten"}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        <Chip size="small" label={channelLabel(conversation)} variant="outlined" sx={{ height: 20 }} />
                        {conversation.contactStatus && (
                          <Chip
                            size="small"
                            label={contactStatusLabel(conversation.contactStatus)}
                            color={contactStatusColor(conversation.contactStatus)}
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(conversation.updatedAt).toLocaleString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                        {conversation.openEscalation && (
                          <Typography variant="caption" color="warning.main" noWrap>
                            · {conversation.openEscalation.reference}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Tooltip title="Konversation löschen">
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ flexShrink: 0 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          void deleteConversation(conversation.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                );
              })}
              {!loading && filteredConversations.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                  <Typography variant="body2" sx={{ mt: 1 }}>Keine passenden Konversationen.</Typography>
                </Box>
              )}
            </List>
          </Paper>

          {/* Detail – auf Mobil nur sichtbar, wenn ein Chat ausgewählt wurde. */}
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: { xs: "calc(100vh - 210px)", sm: "calc(100vh - 230px)", lg: "calc(100vh - 260px)" },
              maxHeight: { lg: "calc(100vh - 260px)" },
              display: isMobile && !conversationDetail ? "none" : "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 1,
              boxShadow: "none",
              borderColor: "divider",
            }}
          >
            {!conversationDetail ? (
              <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, color: "text.secondary", p: 4 }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 56, opacity: 0.3 }} />
                <Typography sx={{ mt: 1 }}>Wähle links eine Konversation aus.</Typography>
              </Stack>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  {isMobile && (
                    <IconButton
                      edge="start"
                      onClick={() => {
                        setSelectedConversationId(null);
                        setConversationDetail(null);
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Avatar
                    sx={{
                      bgcolor: isWhatsappVisitor(conversationDetail.session.visitorId) || hasLinkedWhatsappContact(conversationDetail.session)
                        ? "success.main"
                        : "primary.main",
                      flexShrink: 0,
                      boxShadow: "0 1px 2px rgba(15,23,42,0.18)",
                    }}
                  >
                    {isWhatsappVisitor(conversationDetail.session.visitorId) || hasLinkedWhatsappContact(conversationDetail.session) ? <WhatsAppIcon /> : <PersonIcon />}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {formatVisitor(conversationDetail.session.visitorId, conversationDetail.session.id)}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[conversationDetail.session.status] || conversationDetail.session.status}
                        color={conversationDetail.session.status === "WAITING_HUMAN" ? "warning" : "default"}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        {channelLabel(conversationDetail.session)}
                        {" · "}
                        {new Date(conversationDetail.session.updatedAt).toLocaleString("de-DE")}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      <Chip
                        size="small"
                        label={contactStatusLabel(conversationDetail.session.contactStatus)}
                        color={contactStatusColor(conversationDetail.session.contactStatus)}
                        variant="outlined"
                      />
                      {contactPhoneLabel(conversationDetail.session) && (
                        <Typography variant="caption" color="text.secondary">
                          {contactPhoneLabel(conversationDetail.session)}
                        </Typography>
                      )}
                      {conversationDetail.session.contactStatus === "FAILED" && conversationDetail.session.status === "WAITING_HUMAN" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<WhatsAppIcon />}
                          onClick={retryWhatsappContact}
                        >
                          Erneut senden
                        </Button>
                      )}
                    </Stack>
                  </Box>
                  <Tooltip title="Konversation löschen">
                    <IconButton
                      color="error"
                      sx={{ flexShrink: 0 }}
                      onClick={() => void deleteConversation(conversationDetail.session.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {conversationDetail.messages.map((message) => {
                    const internal = Boolean(message.metadata?.internal);
                    const meta = describeMessage(message.role, message.source, internal);
                    const channel = messageChannelLabel(message);
                    const delivery = messageDeliveryLabel(message);
                    const bubble =
                      meta.variant === "customer"
                        ? { bg: "common.white", border: "grey.300", fg: "text.primary" }
                        : meta.variant === "team"
                        ? { bg: "warning.50", border: "warning.200", fg: "text.primary" }
                        : meta.variant === "system"
                        ? { bg: "grey.200", border: "grey.300", fg: "text.secondary" }
                        : { bg: "primary.main", border: "primary.main", fg: "primary.contrastText" };
                    const Icon =
                      meta.variant === "customer"
                        ? PersonIcon
                        : meta.variant === "team"
                        ? SupportAgentIcon
                        : SmartToyIcon;
                    return (
                      <Stack
                        key={message.id}
                        direction="row"
                        spacing={1}
                        sx={{
                          alignSelf: meta.align === "start" ? "flex-start" : "flex-end",
                          flexDirection: meta.align === "start" ? "row" : "row-reverse",
                          maxWidth: { xs: "100%", sm: "82%", md: "76%" },
                          minWidth: 0,
                        }}
                      >
                        <Avatar sx={{ width: 28, height: 28, bgcolor: bubble.border, color: bubble.fg, flexShrink: 0 }}>
                          <Icon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: bubble.bg,
                            color: bubble.fg,
                            border: "1px solid",
                            borderColor: bubble.border,
                            boxShadow: "0 1px 1px rgba(0,0,0,0.04)",
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.9 }}>
                              {meta.label}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              {new Date(message.createdAt).toLocaleTimeString("de-DE", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {message.content}
                          </Typography>
                          {(channel || delivery) && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                              {channel && <Chip size="small" label={channel} variant="outlined" sx={{ height: 20 }} />}
                              {delivery && (
                                <Chip
                                  size="small"
                                  label={delivery}
                                  color={delivery.includes("Fehler") ? "error" : "success"}
                                  variant="outlined"
                                  sx={{ height: 20 }}
                                />
                              )}
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ p: { xs: 1.5, sm: 2 }, borderTop: "1px solid", borderColor: "divider" }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      placeholder="Manuelle Antwort an den Kunden"
                      value={replyText}
                      onChange={(event) => setReplyText(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          event.preventDefault();
                          void sendManualReply();
                        }
                      }}
                      multiline
                      minRows={1}
                      maxRows={5}
                      fullWidth
                      size="small"
                    />
                    {conversationDetail.session.contactStatus === "ACTIVE" && (
                      <Typography variant="caption" color="text.secondary">
                        Wird im Website-Chat gespeichert und zusätzlich per WhatsApp gesendet.
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<ReplyIcon />}
                    onClick={sendManualReply}
                    disabled={!replyText.trim()}
                    sx={{ alignSelf: { xs: "stretch", sm: "stretch" }, minWidth: { sm: 120 } }}
                  >
                    Senden
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Stack>
      )}

      {tab === 3 && (
        <>
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">Wissensdatenbank</Typography>
                <Typography color="text.secondary">
                  Kurze, belastbare Antworten, die der KI-Agent für wiederkehrende Fragen verwenden darf.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${activeFacts} aktiv`} color="success" variant="outlined" />
                <Chip label={`${facts.length} gesamt`} variant="outlined" />
              </Stack>
            </Stack>
            <Box component="form" onSubmit={submitFact}>
              <Stack spacing={2}>
                <TextField label="Frage" value={factForm.question} onChange={(event) => setFactForm({ ...factForm, question: event.target.value })} required fullWidth />
                <TextField label="Antwort" value={factForm.answer} onChange={(event) => setFactForm({ ...factForm, answer: event.target.value })} required fullWidth multiline minRows={3} />
                <TextField label="Kontext" value={factForm.context} onChange={(event) => setFactForm({ ...factForm, context: event.target.value })} fullWidth multiline minRows={2} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel control={<Checkbox checked={factForm.isActive} onChange={(event) => setFactForm({ ...factForm, isActive: event.target.checked })} />} label="Aktiv" />
                  <Button type="submit" variant="contained">{editingFactId ? "Speichern" : "Anlegen"}</Button>
                  {editingFactId && <Button type="button" onClick={resetFactForm}>Abbrechen</Button>}
                </Stack>
              </Stack>
            </Box>
          </Paper>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Frage</TableCell>
                    <TableCell>Antwort</TableCell>
                    <TableCell>Stand</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facts.map((fact) => (
                    <TableRow key={fact.id} hover>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography fontWeight={700}>{fact.question}</Typography>
                        {fact.context && (
                          <Typography variant="caption" color="text.secondary">{fact.context}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 520 }}>
                        <Typography sx={{ whiteSpace: "pre-wrap" }}>{fact.answer}</Typography>
                      </TableCell>
                      <TableCell>{new Date(fact.updatedAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip size="small" label={fact.isActive ? "Aktiv" : "Inaktiv"} color={fact.isActive ? "success" : "default"} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Bearbeiten">
                          <IconButton onClick={() => {
                            setEditingFactId(fact.id);
                            setFactForm({
                              tenantId,
                              question: fact.question,
                              answer: fact.answer,
                              context: fact.context ?? "",
                              isActive: fact.isActive,
                            });
                          }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {fact.isActive && (
                          <Tooltip title="Deaktivieren">
                            <IconButton color="warning" onClick={() => void deactivateTrainingFact(fact)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title="Endgültig löschen">
                            <IconButton color="error" onClick={() => void permanentlyDeleteTrainingFact(fact)}>
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && facts.length === 0 && (
                    <TableRow><TableCell colSpan={5}>Noch keine Wissenseinträge vorhanden.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

    </Box>
  );
};

export default ChatSettingsPage;
